import type { TelemetryFrame, TelemetryListener, TelemetryProvider } from './TelemetryProvider';

/**
 * RemoteTelemetryProvider — full-quality telemetry (real accelerator pedal,
 * brake, speed, gear) published by the CAN bridge through the WebSocket relay.
 * See bridge/README.md. Reconnects with backoff; reports rate and liveness.
 */

export type RemoteStatus = 'idle' | 'connecting' | 'waiting' | 'ok' | 'stale' | 'error';

export interface RemoteInfo {
  status: RemoteStatus;
  url: string;
  room: string;
  hz: number;
  /** ms since the last telemetry message */
  ageMs: number | null;
  pubs: number | null;
  gear: string | null;
  canOk: boolean;
  /** direct WebRTC channel to the phone bridge is open (telemetry skips the internet) */
  p2p: boolean;
  lastError: string | null;
}

export interface RemoteControlMsg { type: 'control'; car?: string; action?: string; value?: string }

export interface RemoteWireFrame {
  type: 'telemetry';
  t: number;
  speedKmh: number;
  pedal: number;
  brake: number;
  accelMs2: number;
  gear: string;
  torqueNm?: number;
  axleRpm?: number;
  wheel?: string[];
  /** body signals from the CAN bridge (doors, lights, wiper) — see bridge/tesla-can.mjs */
  body?: BodyState;
  bodyEvents?: string[];
  /** TPMS pressures [FL, FR, RL, RR] in bar */
  tires?: number[];
  /** RaceBox 25 Hz GNSS riding the bridge snapshot */
  gps?: { lat: number; lon: number; hAcc?: number };
  gforce?: number[];
  raceboxOk?: boolean;
  /** a generic OBD-II dongle is feeding real speed/pedal */
  obdOk?: boolean;
  /** the car's own engine rpm from PID 010C, and the age of both readings (for the gear learner) */
  engineRpm?: number;
  engineRpmAgeMs?: number;
  speedAgeMs?: number;
  canOk?: boolean;
}
export interface BodyState { doorFL: boolean; doorFR: boolean; doorRL: boolean; doorRR: boolean; lowBeam: boolean; highBeam: boolean; wiper: 'off' | 'one' | 'int' | 'slow' | 'fast' }
export type BodyEvent = `door:${'fl' | 'fr' | 'rl' | 'rr'}:${'open' | 'shut'}` | 'lights:on' | 'lights:off' | `wiper:${'off' | 'one' | 'int' | 'slow' | 'fast'}`;

export type WheelEvent = 'press' | 'tiltLeft' | 'tiltRight' | 'tick+' | 'tick-';

import { relayKeyForRoom, forgetRoomKey } from '../utils/relayKey';

export function defaultRelayUrl(room: string): string {
  if (typeof location === 'undefined') return `ws://localhost:8080/ws/telemetry/${room}`;
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  // dev server (vite :5173) has no relay — assume the Node server on :8080 next to it
  const host = location.port === '5173' ? `${location.hostname}:8080` : location.host;
  return `${proto}://${host}/ws/telemetry/${room}`;
}

export class RemoteTelemetryProvider implements TelemetryProvider {
  private controlListeners: ((m: RemoteControlMsg) => void)[] = [];
  onControl(cb: (m: RemoteControlMsg) => void): () => void {
    this.controlListeners.push(cb);
    return () => { this.controlListeners = this.controlListeners.filter((x) => x !== cb); };
  }

  readonly id = 'can';
  readonly label = 'CAN bridge';
  readonly quality = 'full' as const;

  private ws: WebSocket | null = null;
  private readonly listeners = new Set<TelemetryListener>();
  private wheelListeners = new Set<(e: WheelEvent) => void>();
  private bodyListeners = new Set<(e: BodyEvent, body: BodyState) => void>();
  private prevBody: BodyState | null = null;
  /** door / lights / wiper edge events decoded by the bridge */
  onBody(cb: (e: BodyEvent, body: BodyState) => void): () => void {
    this.bodyListeners.add(cb);
    return () => this.bodyListeners.delete(cb);
  }
  private info: RemoteInfo;
  private lastMsgAt = 0;
  private times: number[] = [];
  private backoff = 500;
  private stopped = true;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  // ---- direct P2P channel to the phone bridge ("local mode") ----
  private pc: RTCPeerConnection | null = null;
  private channel: RTCDataChannel | null = null;
  private lastP2pAt = 0;
  private p2pRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly peerId = Math.random().toString(36).slice(2, 10);

  /** the room's key: the relay refuses the socket without it (it is not derivable from the room name) */
  private key = '';
  private keyPending = false;

  constructor(room: string, private url: string = defaultRelayUrl(room)) {
    this.info = { status: 'idle', url, room, hz: 0, ageMs: null, pubs: null, gear: null, canOk: false, p2p: false, lastError: null };
  }

  setRoom(room: string, url = defaultRelayUrl(room)): void {
    this.url = url;
    this.key = '';
    this.info.room = room;
    this.info.url = url;
    if (!this.stopped) {
      this.stop();
      this.start();
    }
  }

  onWheel(cb: (e: WheelEvent) => void): () => void {
    this.wheelListeners.add(cb);
    return () => this.wheelListeners.delete(cb);
  }

  getInfo(): RemoteInfo {
    const age = this.lastMsgAt ? performance.now() - this.lastMsgAt : null;
    if (this.info.status === 'ok' && age != null && age > 2000) this.info.status = 'stale';
    const span = this.times.length > 1 ? this.times[this.times.length - 1] - this.times[0] : 0;
    return { ...this.info, ageMs: age == null ? null : Math.round(age), hz: span > 0 ? Math.round(((this.times.length - 1) / span) * 1000) : 0 };
  }

  start(): void {
    this.stopped = false;
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    try {
      this.ws?.close();
    } catch {
      /* ignore */
    }
    this.ws = null;
    this.info.status = 'idle';
    this.stopP2p();
  }

  subscribe(listener: TelemetryListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private connect(): void {
    if (this.stopped) return;
    this.info.status = 'connecting';
    if (!this.key) {
      // fetch the room's key once, then come straight back here
      if (!this.keyPending) {
        this.keyPending = true;
        void relayKeyForRoom(this.info.room)
          .then((k) => { this.keyPending = false; this.key = k; if (!this.stopped) this.connect(); })
          .catch((e) => { this.keyPending = false; this.info.lastError = e instanceof Error ? e.message : String(e); this.scheduleReconnect(); });
      }
      return;
    }
    let opened = false;
    let ws: WebSocket;
    try {
      ws = new WebSocket(`${this.url}?role=sub&key=${encodeURIComponent(this.key)}`);
    } catch (e) {
      this.info.status = 'error';
      this.info.lastError = e instanceof Error ? e.message : String(e);
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.backoff = 500;
      this.info.status = 'waiting';
      opened = true;
      this.info.lastError = null;
      this.startP2p();
    };
    ws.onmessage = (ev) => this.onMessage(String(ev.data));
    ws.onerror = () => {
      this.info.lastError = 'socket error';
    };
    ws.onclose = () => {
      if (this.ws === ws) this.ws = null;
      // closed before it ever opened = the relay refused the key (a rotated signing secret does that).
      // Drop it and ask for a new one instead of retrying the same refusal for ever.
      if (!opened) { forgetRoomKey(this.info.room); this.key = ''; }
      if (!this.stopped) {
        this.info.status = 'connecting';
        this.scheduleReconnect();
      }
    };
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, this.backoff);
    this.backoff = Math.min(8000, this.backoff * 2);
  }

  // ---- "local mode": offer a WebRTC data channel through the relay; if the phone bridge answers and
  // ICE finds the hotspot LAN (host candidates only — no STUN, LAN-only by design), telemetry flows
  // directly phone → browser and the relay copies are ignored. Any failure just leaves the relay path.
  private startP2p(): void {
    if (this.stopped || this.pc) return;
    if (typeof RTCPeerConnection === 'undefined') return;
    try {
      const pc = new RTCPeerConnection({ iceServers: [] });
      this.pc = pc;
      const ch = pc.createDataChannel('telemetry', { ordered: false, maxRetransmits: 0 });
      this.channel = ch;
      ch.onmessage = (ev) => this.onMessage(String(ev.data), true);
      ch.onclose = () => { if (this.channel === ch) this.restartP2pLater(); };
      pc.onicecandidate = (ev) => {
        if (ev.candidate) this.sendSignal({ type: 'rtc:ice', from: this.peerId, candidate: ev.candidate.candidate, sdpMid: ev.candidate.sdpMid, sdpMLineIndex: ev.candidate.sdpMLineIndex });
      };
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'failed' || pc.connectionState === 'closed') this.restartP2pLater();
      };
      void pc.createOffer().then(async (offer) => {
        await pc.setLocalDescription(offer);
        this.sendSignal({ type: 'rtc:offer', from: this.peerId, sdp: offer.sdp });
      }).catch(() => this.restartP2pLater());
      // no answer in 20 s → tear down and retry (the phone may join the room later)
      this.p2pRetryTimer = setTimeout(() => { if (pc.connectionState !== 'connected') this.restartP2pLater(); }, 20000);
    } catch {
      this.pc = null;
      this.channel = null;
    }
  }

  private async onRtcSignal(msg: { type: string; to?: string; sdp?: string; candidate?: string; sdpMid?: string | null; sdpMLineIndex?: number | null }): Promise<void> {
    if (msg.to !== this.peerId || !this.pc) return;
    try {
      if (msg.type === 'rtc:answer' && msg.sdp) await this.pc.setRemoteDescription({ type: 'answer', sdp: msg.sdp });
      else if (msg.type === 'rtc:ice' && msg.candidate) await this.pc.addIceCandidate({ candidate: msg.candidate, sdpMid: msg.sdpMid ?? undefined, sdpMLineIndex: msg.sdpMLineIndex ?? undefined });
    } catch { this.restartP2pLater(); }
  }

  private sendSignal(msg: Record<string, unknown>): void {
    try { if (this.ws?.readyState === 1) this.ws.send(JSON.stringify(msg)); } catch { /* ignore */ }
  }

  private stopP2p(): void {
    if (this.p2pRetryTimer) clearTimeout(this.p2pRetryTimer);
    this.p2pRetryTimer = null;
    try { this.channel?.close(); } catch { /* ignore */ }
    try { this.pc?.close(); } catch { /* ignore */ }
    this.channel = null;
    this.pc = null;
    this.info.p2p = false;
  }

  private restartP2pLater(): void {
    this.stopP2p();
    if (this.stopped || this.p2pRetryTimer) return;
    this.p2pRetryTimer = setTimeout(() => { this.p2pRetryTimer = null; this.startP2p(); }, 15000);
  }

  private onMessage(raw: string, viaP2p = false): void {
    let msg: Omit<Partial<RemoteWireFrame>, 'type'> & { type?: string; pubs?: number };
    try {
      msg = JSON.parse(raw);
    } catch {
      return;
    }
    if (msg.type === 'relay') {
      this.info.pubs = msg.pubs ?? null;
      return;
    }
    if (typeof msg.type === 'string' && msg.type.startsWith('rtc:')) {
      void this.onRtcSignal(msg as unknown as { type: string; to?: string; sdp?: string; candidate?: string; sdpMid?: string | null; sdpMLineIndex?: number | null });
      return;
    }
    if (msg.type === 'control') {
      // remote control from the phone app: { type:'control', car?: id, action?: 'home'|'start' }
      this.controlListeners.forEach((cb) => cb(msg as unknown as RemoteControlMsg));
      return;
    }
    if (msg.type !== 'telemetry' || typeof msg.speedKmh !== 'number') return;
    const now = performance.now();
    if (!viaP2p && now - this.lastP2pAt < 1200) return; // the direct channel is live — the relay copy is the late duplicate
    if (viaP2p) this.lastP2pAt = now;
    this.info.p2p = now - this.lastP2pAt < 1200;
    this.lastMsgAt = now;
    this.times.push(now);
    if (this.times.length > 50) this.times.shift();
    this.info.status = 'ok';
    this.info.gear = msg.gear ?? null;
    this.info.canOk = !!msg.canOk;
    for (const w of msg.wheel ?? []) this.wheelListeners.forEach((cb) => cb(w as WheelEvent));
    if (msg.body) {
      // events the bridge noticed, plus events derived from state changes — a bridge that ships
      // stale/empty bodyEvents (the app did, and edge frames can drop) must not mute the HUD
      const events = new Set<string>(msg.bodyEvents ?? []);
      const prev = this.prevBody;
      const b = msg.body as BodyState;
      if (prev) {
        for (const [k, tag] of [['doorFL', 'fl'], ['doorFR', 'fr'], ['doorRL', 'rl'], ['doorRR', 'rr']] as const) {
          if (b[k] !== prev[k]) events.add(`door:${tag}:${b[k] ? 'open' : 'shut'}`);
        }
        const on = b.lowBeam || b.highBeam, was = prev.lowBeam || prev.highBeam;
        if (on !== was) events.add(on ? 'lights:on' : 'lights:off');
        if (b.wiper !== prev.wiper) events.add(`wiper:${b.wiper}`);
      }
      this.prevBody = { ...b };
      for (const e of events) this.bodyListeners.forEach((cb) => cb(e as BodyEvent, b));
    }
    if (msg.canOk === false && !msg.raceboxOk && !msg.obdOk) return; // bridge alive but no data source → let the session hold/decay
    const frame: TelemetryFrame = {
      t: now,
      speedKmh: Math.max(0, msg.speedKmh),
      accelMs2: typeof msg.accelMs2 === 'number' ? msg.accelMs2 : null,
      pedal: typeof msg.pedal === 'number' ? msg.pedal : null,
      brake: typeof msg.brake === 'number' ? msg.brake : null,
      source: 'can',
      gear: typeof msg.gear === 'string' ? msg.gear : null,
      tires: Array.isArray(msg.tires) && msg.tires.length === 4 ? (msg.tires as number[]) : null,
      gps: msg.gps && typeof msg.gps.lat === 'number' && typeof msg.gps.lon === 'number' ? { lat: msg.gps.lat, lon: msg.gps.lon, hAccM: msg.gps.hAcc ?? null } : null,
      engineRpm: typeof msg.engineRpm === 'number' ? msg.engineRpm : null,
      engineRpmAgeMs: typeof msg.engineRpmAgeMs === 'number' ? msg.engineRpmAgeMs : null,
      speedAgeMs: typeof msg.speedAgeMs === 'number' ? msg.speedAgeMs : null,
    };
    this.listeners.forEach((l) => l(frame));
  }
}
